import React, { useEffect, useState } from 'react';
import { Plus, Play, Trash2, Loader2, BookOpen, AlertCircle, MoreHorizontal } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  useStructureTypes,
  useStructureTasks,
  useStructureTaskModules,
  useCreateStructureType,
  useCreateStructureTask,
  useUpdateStructureTask,
  useCreateStructureTaskModule,
  useDeleteStructureType,
  useDeleteStructureTask,
  useDeleteStructureTaskModule,
  useMarkStructureTaskModuleWatched,
  useUpdateStructureTaskModule,
} from '../hooks';
import {
  CreateStructureTypeForm,
  CreateStructureTaskForm,
  CreateStructureTaskModuleForm,
} from '@/shared/forms/client-level';
import { VideoPlayerModal } from '@/shared/components/VideoPlayerModal';
import { TRAINING_TOUR_ID } from '@/features/tours/training';
import { useTour } from '@/features/tours';
import { getStructureTaskModulePlaybackUrl, getClientUsersForTraining } from '../api';
import { useUser } from '@/shared/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import type {
  StructureType,
  StructureTask,
  StructureTaskModule,
  StructureTypeFormData,
  StructureTaskFormData,
  StructureTaskModuleFormData,
} from '../types';

type DialogType =
  | { kind: 'structure-type' }
  | { kind: 'structure-task'; defaultStructureTypeId?: string }
  | { kind: 'module'; defaultTaskId?: string }
  | { kind: 'edit-task'; task: StructureTask }
  | { kind: 'edit-module'; module: StructureTaskModule }
  | null;

type DeleteTarget =
  | { kind: 'structure-type'; id: string; name: string }
  | { kind: 'structure-task'; id: string; name: string }
  | { kind: 'module'; id: string; name: string }
  | null;

const ModuleCard: React.FC<{
  module: StructureTaskModule;
  onPlay: (module: StructureTaskModule) => void;
  onDownload: (module: StructureTaskModule) => void;
  onEdit: (module: StructureTaskModule) => void;
  onDelete: (module: StructureTaskModule) => void;
  canManageModules: boolean;
  currentUserId: number | null;
}> = ({
  module,
  onPlay,
  onDownload,
  onEdit,
  onDelete,
  canManageModules,
  currentUserId,
}) => {
  const isAssignedToCurrentUser = Boolean(
    currentUserId &&
    module.required_users?.some((requiredUser) => requiredUser.id === currentUserId),
  );
  const isCompletedByCurrentUser = Boolean(module.watched_by_current_user);

  return (
    <div className="group flex h-full flex-col rounded-lg border bg-card p-3 transition-colors hover:bg-accent/30">
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium">{module.name}</p>
        {isAssignedToCurrentUser && (
          <div className="mb-2 mt-1 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              Required
            </Badge>
            <Badge
              variant={isCompletedByCurrentUser ? 'default' : 'secondary'}
              className="text-[10px] uppercase tracking-wide"
            >
              {isCompletedByCurrentUser ? 'Completed' : 'Pending'}
            </Badge>
          </div>
        )}
        {module.description && (
          <p className="line-clamp-3 text-xs text-muted-foreground">{module.description}</p>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between gap-1">
        <Button
          variant={isAssignedToCurrentUser ? 'default' : 'outline'}
          size="sm"
          className="h-8 flex-1 px-3"
          onClick={() => onPlay(module)}
          disabled={!module.video}
        >
          <Play className="mr-1 h-3.5 w-3.5" />
          {isAssignedToCurrentUser ? 'Start' : 'Play'}
        </Button>
        {canManageModules && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Module actions">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownload(module)} disabled={!module.video}>
                Download Module
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(module)}>Edit Module</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={() => onDelete(module)}>
                Delete Module
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

const AddCardButton: React.FC<{
  label: string;
  onClick: () => void;
  className?: string;
}> = ({ label, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex h-full min-h-[150px] w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 p-3 text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary ${className}`}
  >
    <span className="mb-1 flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-current">
      <Plus className="h-4 w-4" />
    </span>
    <span className="text-xs font-medium">{label}</span>
  </button>
);

const TaskSection: React.FC<{
  task: StructureTask;
  modules: StructureTaskModule[];
  onAddModule: (taskId: string) => void;
  onPlayModule: (module: StructureTaskModule) => void;
  onDownloadModule: (module: StructureTaskModule) => void;
  onEditTask: (task: StructureTask) => void;
  onEditModule: (module: StructureTaskModule) => void;
  onDeleteModule: (module: StructureTaskModule) => void;
  onDeleteTask: (task: StructureTask) => void;
  canManageModules: boolean;
  currentUserId: number | null;
}> = ({
  task,
  modules,
  onAddModule,
  onPlayModule,
  onDownloadModule,
  onEditTask,
  onEditModule,
  onDeleteModule,
  onDeleteTask,
  canManageModules,
  currentUserId,
}) => {
  const taskModules = modules.filter((m) => m.task === task.id);

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="truncate text-sm font-semibold text-foreground">{task.name}</h4>
          <Badge variant="secondary" className="mt-1 text-xs">
            {taskModules.length} {taskModules.length === 1 ? 'video' : 'videos'}
          </Badge>
        </div>
        {canManageModules && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Task actions">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditTask(task)}>Edit Task</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={() => onDeleteTask(task)}>
                Delete Task
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {taskModules.length > 0 || canManageModules ? (
        <div className="space-y-2">
          {taskModules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              onPlay={onPlayModule}
              onDownload={onDownloadModule}
              onEdit={onEditModule}
              onDelete={onDeleteModule}
              canManageModules={canManageModules}
              currentUserId={currentUserId}
            />
          ))}
          {canManageModules && (
            <AddCardButton label="Add Video" onClick={() => onAddModule(task.id)} className="min-h-[100px]" />
          )}
        </div>
      ) : (
        <div className="py-2 text-xs italic text-muted-foreground">No training videos yet.</div>
      )}
    </div>
  );
};

const StructureTypeSection: React.FC<{
  structureType: StructureType;
  tasks: StructureTask[];
  modules: StructureTaskModule[];
  onAddTask: (structureTypeId: string) => void;
  onAddModule: (taskId: string) => void;
  onPlayModule: (module: StructureTaskModule) => void;
  onDownloadModule: (module: StructureTaskModule) => void;
  onEditTask: (task: StructureTask) => void;
  onEditModule: (module: StructureTaskModule) => void;
  onDeleteModule: (module: StructureTaskModule) => void;
  onDeleteTask: (task: StructureTask) => void;
  onDeleteStructureType: (structureType: StructureType) => void;
  canManageModules: boolean;
  currentUserId: number | null;
}> = ({
  structureType,
  tasks,
  modules,
  onAddTask,
  onAddModule,
  onPlayModule,
  onDownloadModule,
  onEditTask,
  onEditModule,
  onDeleteModule,
  onDeleteTask,
  onDeleteStructureType,
  canManageModules,
  currentUserId,
}) => {
  const typeTasks = tasks.filter((t) => t.structure_type === structureType.id);

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b bg-muted/40 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{structureType.name}</h3>
            <p className="text-xs text-muted-foreground">
              {typeTasks.length} {typeTasks.length === 1 ? 'task' : 'tasks'}
            </p>
          </div>
        </div>
        {canManageModules && (
          <div className="flex w-full items-center justify-end gap-1 sm:w-auto sm:justify-start">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => onDeleteStructureType(structureType)}
              title="Delete structure type"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
      <div className="p-5">
        {typeTasks.length > 0 || canManageModules ? (
          <div className="space-y-3">
            {typeTasks.map((task) => (
              <TaskSection
                key={task.id}
                task={task}
                modules={modules}
                onAddModule={onAddModule}
                onPlayModule={onPlayModule}
                onDownloadModule={onDownloadModule}
                onEditTask={onEditTask}
                onEditModule={onEditModule}
                onDeleteModule={onDeleteModule}
                onDeleteTask={onDeleteTask}
                canManageModules={canManageModules}
                currentUserId={currentUserId}
              />
            ))}
            {canManageModules && (
              <AddCardButton
                label="Add Task"
                onClick={() => onAddTask(structureType.id)}
                className="min-h-[120px]"
              />
            )}
          </div>
        ) : (
          <div className="py-6 text-center text-sm text-muted-foreground">No tasks yet.</div>
        )}
      </div>
    </div>
  );
};

const TrainingPage: React.FC = () => {
  const { user } = useUser();
  const { clientName } = useParams<{ clientName: string }>();
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);
  const [playingModule, setPlayingModule] = useState<StructureTaskModule | null>(null);
  const [playingModuleId, setPlayingModuleId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const { data: structureTypes = [], isLoading: isLoadingTypes, error: typesError } = useStructureTypes();
  const { data: tasks = [], isLoading: isLoadingTasks } = useStructureTasks();
  const { data: modules = [], isLoading: isLoadingModules } = useStructureTaskModules();

  const createStructureType = useCreateStructureType();
  const createStructureTask = useCreateStructureTask();
  const updateStructureTask = useUpdateStructureTask();
  const createStructureTaskModule = useCreateStructureTaskModule();
  const updateStructureTaskModule = useUpdateStructureTaskModule();
  const deleteStructureType = useDeleteStructureType();
  const deleteStructureTask = useDeleteStructureTask();
  const deleteStructureTaskModule = useDeleteStructureTaskModule();
  const markModuleWatched = useMarkStructureTaskModuleWatched();

  const clientId = user?.client_info?.id ? Number(user.client_info.id) : undefined;
  const { data: clientUsersData } = useQuery({
    queryKey: ['training-client-users', clientId],
    queryFn: async () => {
      if (!clientId) return { users: [] };
      const response = await getClientUsersForTraining(clientId);
      return response.data;
    },
    enabled: Boolean(clientId),
  });
  const clientUsers = clientUsersData?.users ?? [];
  const currentUserId = user?.id ? Number(user.id) : null;
  const canManageModules = Boolean(user?.is_client_admin);
  const canAccessTrainingManagement = canManageModules;

  const isLoading = isLoadingTypes || isLoadingTasks || isLoadingModules;

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (isLoading) return;
    if (hasSeen(TRAINING_TOUR_ID)) return;
    void startTour(TRAINING_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, isLoading]);

  const handleCreateStructureType = async (data: StructureTypeFormData) => {
    await createStructureType.mutateAsync(data);
    setActiveDialog(null);
  };

  const handleCreateStructureTask = async (data: StructureTaskFormData) => {
    await createStructureTask.mutateAsync(data);
    setActiveDialog(null);
  };

  const handleCreateModule = async (data: StructureTaskModuleFormData) => {
    if (!data.video) return;
    setUploadProgress(0);
    await createStructureTaskModule.mutateAsync({
      name: data.name,
      description: data.description,
      task: data.task,
      video: data.video,
      required_user_ids: data.required_user_ids,
      onProgress: setUploadProgress,
    });
    setActiveDialog(null);
    setUploadProgress(0);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    if (deleteTarget.kind === 'module') {
      deleteStructureTaskModule.mutate(deleteTarget.id);
    } else if (deleteTarget.kind === 'structure-task') {
      deleteStructureTask.mutate(deleteTarget.id);
    } else if (deleteTarget.kind === 'structure-type') {
      deleteStructureType.mutate(deleteTarget.id);
    }

    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </BaseLayout>
    );
  }

  if (typesError) {
    return (
      <BaseLayout>
        <div className="p-6">
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm">Failed to load training modules.</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Training</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage how-to training videos organized by structure type and task.
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {canAccessTrainingManagement && clientName && (
              <Button asChild variant="outline" className="w-full gap-2 sm:w-auto" data-tour-id="training-manage">
                <Link to={`/app/training/${clientName}/management`}>Manage Modules</Link>
              </Button>
            )}
            {canManageModules && (
              <Button
                onClick={() => setActiveDialog({ kind: 'structure-type' })}
                className="w-full gap-2 sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                Add Structure Type
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {structureTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-1">No training modules yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              Get started by adding a structure type, then create tasks and upload training videos.
            </p>
            {canManageModules && (
              <Button onClick={() => setActiveDialog({ kind: 'structure-type' })} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Structure Type
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {structureTypes.map((st, idx) => (
              <div
                key={st.id}
                data-tour-id={idx === 0 ? 'training-structure-type' : undefined}
              >
              <StructureTypeSection
                structureType={st}
                tasks={tasks}
                modules={modules}
                onAddTask={(id) => setActiveDialog({ kind: 'structure-task', defaultStructureTypeId: id })}
                onAddModule={(taskId) => setActiveDialog({ kind: 'module', defaultTaskId: taskId })}
                onEditTask={(task) => setActiveDialog({ kind: 'edit-task', task })}
                onEditModule={(module) => setActiveDialog({ kind: 'edit-module', module })}
                onPlayModule={async (module) => {
                  try {
                    setPlayingModuleId(module.id);
                    const response = await getStructureTaskModulePlaybackUrl(module.id);
                    setPlayingModule({
                      ...module,
                      video_url: response.data.playback_url,
                    });
                  } catch (error) {
                    void error;
                    setPlayingModule(module);
                  } finally {
                    setPlayingModuleId(null);
                  }
                }}
                onDownloadModule={async (module) => {
                  try {
                    const response = await getStructureTaskModulePlaybackUrl(module.id);
                    const fileResponse = await fetch(response.data.playback_url);
                    if (!fileResponse.ok) {
                      throw new Error('Failed to fetch module video for download.');
                    }
                    const blob = await fileResponse.blob();
                    const objectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = `${module.name}.mp4`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl);
                  } catch (error) {
                    void error;
                  }
                }}
                onDeleteModule={(module) =>
                  setDeleteTarget({ kind: 'module', id: module.id, name: module.name })
                }
                onDeleteTask={(task) =>
                  setDeleteTarget({ kind: 'structure-task', id: task.id, name: task.name })
                }
                onDeleteStructureType={(structureType) =>
                  setDeleteTarget({
                    kind: 'structure-type',
                    id: structureType.id,
                    name: structureType.name,
                  })
                }
                canManageModules={canManageModules}
                currentUserId={currentUserId}
              />
              </div>
            ))}
          </div>
        )}
      </div>

      {canManageModules && (
        <>
          {/* Delete Confirmation Dialog */}
          <Dialog
            open={Boolean(deleteTarget)}
            onOpenChange={(open) => {
              if (!open) setDeleteTarget(null);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogDescription>
                  {deleteTarget
                    ? `This will permanently delete "${deleteTarget.name}". This action cannot be undone.`
                    : 'This action cannot be undone.'}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={
                    deleteStructureType.isPending ||
                    deleteStructureTask.isPending ||
                    deleteStructureTaskModule.isPending
                  }
                >
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Create Structure Type Dialog */}
          <Dialog
            open={activeDialog?.kind === 'structure-type'}
            onOpenChange={(open) => !open && setActiveDialog(null)}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Structure Type</DialogTitle>
                <DialogDescription>
                  Choose a preset structure type or create a custom one for your organization.
                </DialogDescription>
              </DialogHeader>
              <CreateStructureTypeForm
                existingStructureTypeNames={structureTypes.map((structureType) => structureType.name)}
                onSubmit={handleCreateStructureType}
                onCancel={() => setActiveDialog(null)}
                isLoading={createStructureType.isPending}
              />
            </DialogContent>
          </Dialog>

          {/* Create Structure Task Dialog */}
          <Dialog
            open={activeDialog?.kind === 'structure-task'}
            onOpenChange={(open) => !open && setActiveDialog(null)}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Task</DialogTitle>
                <DialogDescription>
                  Create a construction task to associate training videos with.
                </DialogDescription>
              </DialogHeader>
              <CreateStructureTaskForm
                structureTypes={structureTypes}
                defaultStructureTypeId={
                  activeDialog?.kind === 'structure-task' ? activeDialog.defaultStructureTypeId : undefined
                }
                onSubmit={handleCreateStructureTask}
                onCancel={() => setActiveDialog(null)}
                isLoading={createStructureTask.isPending}
              />
            </DialogContent>
          </Dialog>

          {/* Create Module Dialog */}
          <Dialog
            open={activeDialog?.kind === 'module'}
            onOpenChange={(open) => !open && setActiveDialog(null)}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Upload Training Video</DialogTitle>
                <DialogDescription>
                  Upload a how-to video. The file goes directly to storage — no size limits.
                </DialogDescription>
              </DialogHeader>
              <CreateStructureTaskModuleForm
                tasks={tasks}
                clientUsers={clientUsers}
                defaultTaskId={
                  activeDialog?.kind === 'module' ? activeDialog.defaultTaskId : undefined
                }
                onSubmit={handleCreateModule}
                onCancel={() => setActiveDialog(null)}
                isLoading={createStructureTaskModule.isPending}
                uploadProgress={uploadProgress}
              />
            </DialogContent>
          </Dialog>

          {/* Edit Task Dialog */}
          <Dialog
            open={activeDialog?.kind === 'edit-task'}
            onOpenChange={(open) => !open && setActiveDialog(null)}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Task</DialogTitle>
                <DialogDescription>
                  Update task details and associated structure type.
                </DialogDescription>
              </DialogHeader>
              {activeDialog?.kind === 'edit-task' && (
                <CreateStructureTaskForm
                  structureTypes={structureTypes}
                  initialData={{
                    name: activeDialog.task.name,
                    structure_type: activeDialog.task.structure_type,
                  }}
                  submitLabel="Save Task"
                  onSubmit={async (data) => {
                    await updateStructureTask.mutateAsync({
                      id: activeDialog.task.id,
                      data,
                    });
                    setActiveDialog(null);
                  }}
                  onCancel={() => setActiveDialog(null)}
                  isLoading={updateStructureTask.isPending}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Module Dialog */}
          <Dialog
            open={activeDialog?.kind === 'edit-module'}
            onOpenChange={(open) => !open && setActiveDialog(null)}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Training Module</DialogTitle>
                <DialogDescription>
                  Update module details. Upload a new video only if you want to replace the existing one.
                </DialogDescription>
              </DialogHeader>
              {activeDialog?.kind === 'edit-module' && (
                <CreateStructureTaskModuleForm
                  tasks={tasks}
                  clientUsers={clientUsers}
                  initialData={{
                    name: activeDialog.module.name,
                    description: activeDialog.module.description,
                    task: activeDialog.module.task,
                    required_user_ids: (activeDialog.module.required_users ?? []).map((u) => u.id),
                  }}
                  requireVideo={false}
                  submitLabel="Save Changes"
                  onSubmit={async (data) => {
                    setUploadProgress(0);
                    await updateStructureTaskModule.mutateAsync({
                      id: activeDialog.module.id,
                      name: data.name,
                      description: data.description,
                      task: data.task,
                      required_user_ids: data.required_user_ids,
                      video: data.video,
                      onProgress: setUploadProgress,
                    });
                    setActiveDialog(null);
                    setUploadProgress(0);
                  }}
                  onCancel={() => setActiveDialog(null)}
                  isLoading={updateStructureTaskModule.isPending}
                  uploadProgress={uploadProgress}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Video Player Modal */}
      {playingModuleId && (
        <div className="fixed bottom-4 right-4 z-50 rounded-md bg-background border px-3 py-2 shadow">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading video...
          </div>
        </div>
      )}

      {playingModule && (
        // Lock seeking only when this is a required module for current user
        // and they have not completed it yet.
        // If already watched, user can scrub freely.
        <VideoPlayerModal
          title={playingModule.name}
          description={playingModule.description}
          videoUrl={playingModule.video_url}
          onClose={() => setPlayingModule(null)}
          onEnded={() => {
            markModuleWatched.mutate({ id: playingModule.id, currentUserId });
            setPlayingModule((prev) =>
              prev
                ? {
                    ...prev,
                    watched_by_current_user: true,
                    watched_at_current_user: new Date().toISOString(),
                  }
                : prev,
            );
          }}
          lockSeeking={
            Boolean(
              currentUserId &&
              playingModule.required_users?.some((requiredUser) => requiredUser.id === currentUserId) &&
              !playingModule.watched_by_current_user,
            )
          }
          lockSeekingMessage="Fast-forward and skip are disabled for this required training video."
        />
      )}
    </BaseLayout>
  );
};

export default TrainingPage;
